import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { formatDate } from '@/utils/dateFormatting';
import { getRoleDisplayName } from '@/utils/rolePermissions';
import { type FamilyUserSummary, getFamilyUserDisplayName, getFamilyUserSecondaryLine } from '@/types/family-user';

import './family-user-details-modal.css';

interface FamilyUserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: string;
  users: FamilyUserSummary[];
  canViewEmails: boolean;
  /** When the API returns counts but no user list for this role, pass the count for a helpful message */
  countForRole?: number;
}

const FamilyUserDetailsModal: React.FC<FamilyUserDetailsModalProps> = ({
  isOpen,
  onClose,
  role,
  users,
  canViewEmails,
  countForRole
}) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose]
  );

  const roleDisplayName = getRoleDisplayName(role);

  const emptyMessage =
    users.length === 0 && countForRole != null && countForRole > 0
      ? t('userDetails.emptyUnavailable')
      : users.length === 0
        ? t('userDetails.emptyNone')
        : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
          <Dialog.Content
            className="v01-modal v01-modal--panel"
            aria-labelledby="family-user-details-modal-title"
            aria-describedby="family-user-details-modal-desc"
          >
            <h2 id="family-user-details-modal-title" className="v01-modal__title">
              {t('userDetails.title', { role: roleDisplayName })}
            </h2>
            <p id="family-user-details-modal-desc" className="v01-modal__desc">
              {emptyMessage !== null ? emptyMessage : t('userDetails.count', { count: users.length })}
            </p>
            {users.length > 0 && (
              <div className="family-user-details-modal-list">
                {users.map((user) => {
                  const secondary = getFamilyUserSecondaryLine(user, canViewEmails);

                  return (
                    <div key={user.userId} className="family-user-details-modal-row">
                      <div className="family-user-details-modal-row-primary">
                        <span className="family-user-details-modal-nickname">{getFamilyUserDisplayName(user)}</span>
                        {secondary && <span className="family-user-details-modal-email">{secondary}</span>}
                      </div>
                      <div className="family-user-details-modal-row-secondary">
                        {formatDate(user.membershipCreatedAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="family-user-details-modal-actions">
              <button type="button" onClick={onClose} className="btn btn--primary">
                {tCommon('close')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default FamilyUserDetailsModal;
